/**
 * Frontend-Logger (LOGGING_CONTRACT §2 / §4).
 *
 * - Strukturierte JSON-Form: { level, time, msg, source:"client", requestId?, context? }
 * - Jeder Eintrag geht an die console (structured).
 * - Bei warn/error zusätzlich best-effort-POST an /api/client-log.
 *   Ein fehlgeschlagener POST wirft NIEMALS; er beeinflusst die UI nicht.
 * - Throttle: maximal LOG_THROTTLE_PER_WINDOW (10) warn/error-POSTs
 *   pro LOG_THROTTLE_WINDOW_MS (30 s), danach Schweigen bis zum nächsten Fenster.
 * - window.onerror + unhandledrejection werden abgefangen und als error geloggt.
 */

// ---- Typen ------------------------------------------------------------------

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Verbindliche Form aus §2. */
interface LogEntry {
  level: LogLevel;
  time: number;
  msg: string;
  source: 'client';
  requestId?: string;
  context?: Record<string, unknown>;
}

/** Body für POST /api/client-log (§4). */
interface ClientLogBody {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  url?: string;
  clientRequestId?: string;
}

// ---- Log-Level-Vergleich ----------------------------------------------------

const LEVEL_RANK: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

/** Aktuell aktiver Mindest-Level aus LOG_LEVEL / VITE_LOG_LEVEL; Default: 'info'. */
function activeLevel(): LogLevel {
  // Vite-Umgebungsvariable (compile-time); Fallback auf 'info'.
  const raw =
    (typeof import.meta !== 'undefined' &&
      (import.meta.env['VITE_LOG_LEVEL'] as string | undefined)) ||
    'info';
  return (['debug', 'info', 'warn', 'error'].includes(raw) ? raw : 'info') as LogLevel;
}

// ---- Throttle ---------------------------------------------------------------

const LOG_THROTTLE_WINDOW_MS = 30_000;
const LOG_THROTTLE_PER_WINDOW = 10;

let throttleWindowStart = Date.now();
let throttleCount = 0;

/** Gibt true zurück, wenn dieser POST durchgelassen werden soll. */
function checkThrottle(): boolean {
  const now = Date.now();
  if (now - throttleWindowStart > LOG_THROTTLE_WINDOW_MS) {
    throttleWindowStart = now;
    throttleCount = 0;
  }
  if (throttleCount >= LOG_THROTTLE_PER_WINDOW) return false;
  throttleCount++;
  return true;
}

// ---- Letzte gesehene x-request-id ------------------------------------------

let _lastRequestId: string | undefined;

/** Wird von apiFetch nach jedem Response aufgerufen, um die ID zu speichern. */
export function setLastRequestId(id: string | undefined): void {
  _lastRequestId = id || undefined;
}

// ---- Kern-Logger ------------------------------------------------------------

/** CSRF-Cookie für den Client-Log-POST auslesen (Double-Submit). */
function readCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Best-effort-POST an /api/client-log.
 * Darf NIEMALS werfen. Keine Rekursion: ruft sich selbst nicht nochmals auf.
 */
async function postClientLog(body: ClientLogBody): Promise<void> {
  try {
    const csrf = readCsrfToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (csrf) headers['X-CSRF-Token'] = csrf;
    await fetch('/api/client-log', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
  } catch {
    // Bewusst verschluckt — niemals werfen, niemals rekursiv loggen.
  }
}

function writeLog(
  level: LogLevel,
  msg: string,
  context?: Record<string, unknown>,
  requestId?: string,
): void {
  // Level-Filter
  if (LEVEL_RANK[level] < LEVEL_RANK[activeLevel()]) return;

  const entry: LogEntry = {
    level,
    time: Date.now(),
    msg,
    source: 'client',
    ...(requestId ? { requestId } : {}),
    ...(context && Object.keys(context).length > 0 ? { context } : {}),
  };

  // Console-Ausgabe (strukturiert)
  const consoleFn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'debug'
          ? console.debug
          : console.info;
  consoleFn('[client]', JSON.stringify(entry));

  // Bei warn/error: best-effort-POST (throttled)
  if (level === 'warn' || level === 'error') {
    if (!checkThrottle()) return;
    const clientRequestId = requestId ?? _lastRequestId;
    void postClientLog({
      level,
      message: msg,
      ...(context && Object.keys(context).length > 0 ? { context } : {}),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...(clientRequestId ? { clientRequestId } : {}),
    });
  }
}

// ---- Singleton `log` --------------------------------------------------------

export const log = {
  debug(msg: string, context?: Record<string, unknown>): void {
    writeLog('debug', msg, context);
  },
  info(msg: string, context?: Record<string, unknown>): void {
    writeLog('info', msg, context);
  },
  warn(msg: string, context?: Record<string, unknown>): void {
    writeLog('warn', msg, context);
  },
  error(msg: string, context?: Record<string, unknown>, requestId?: string): void {
    writeLog('error', msg, context, requestId);
  },
};

// ---- Globale uncaught-Error-Handler -----------------------------------------

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    log.error('Uncaught error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      errorName: event.error instanceof Error ? event.error.name : undefined,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    log.error('Unhandled promise rejection', {
      reason: reason instanceof Error ? reason.message : String(reason),
      errorName: reason instanceof Error ? reason.name : undefined,
    });
  });
}
